import { getCmsBasePath } from "./workspaceRoutes";
import type { AuthExtras } from "../types/auth";
import {
  getAccountPath,
  getEventPath,
  getSpacePath,
  toAccountId,
  toSpaceId,
  type AccountCollectionId,
  type EventCollectionId,
  type SpaceCollectionId,
} from "./collectionIds";

export type AdminMode =
  | { kind: "hub" }
  | { kind: "global" }
  | { kind: "account"; id: AccountCollectionId }
  | { kind: "space"; id: SpaceCollectionId }
  | { kind: "event"; id: EventCollectionId };

export type WorkspaceParams = {
  accountId?: string;
  spaceId?: string;
  eventId?: string;
};

export function getFirestoreBasePath(mode: AdminMode): string {
  switch (mode.kind) {
    case "hub":
    case "global":
      return "";
    case "account":
      return getAccountPath(mode.id);
    case "space":
      return getSpacePath(mode.id);
    case "event":
      return getEventPath(mode.id);
  }
}

export function resolveAdminMode(params: WorkspaceParams): AdminMode {
  const { accountId, spaceId, eventId } = params;

  if (eventId && accountId) {
    return {
      kind: "event",
      id: { accountId, spaceId: spaceId ?? "", eventId },
    };
  }

  if (spaceId && accountId) {
    return { kind: "space", id: { accountId, spaceId } };
  }

  if (accountId) {
    return { kind: "account", id: { accountId } };
  }

  return { kind: "global" };
}

/**
 * Cheap gate before panel verify / collection build.
 * Account/space/event only require loaded extras — scoped membership is
 * enforced by `verifyPanelAccess`, not by coarse hasAny flags.
 */
export function canAccessMode(
  mode: AdminMode,
  extra: AuthExtras | undefined
): boolean {
  if (!extra) {
    return false;
  }

  switch (mode.kind) {
    case "hub":
    case "account":
    case "space":
    case "event":
      return true;
    case "global":
      return extra.isSuperManager;
  }
}

export function getWorkspaceKey(mode: AdminMode): string {
  return getCmsBasePath(mode);
}

export function modeAccountId(mode: AdminMode): string | undefined {
  if (mode.kind === "hub" || mode.kind === "global") {
    return undefined;
  }
  return mode.id.accountId;
}

export function modeSpaceId(mode: AdminMode): string | undefined {
  if (mode.kind === "space" || mode.kind === "event") {
    return mode.id.spaceId;
  }
  return undefined;
}

export function modeEventId(mode: AdminMode): string | undefined {
  return mode.kind === "event" ? mode.id.eventId : undefined;
}

export type ModeCollectionIds = {
  account?: AccountCollectionId;
  space?: SpaceCollectionId;
  event?: EventCollectionId;
};

export function getModeCollectionIds(mode: AdminMode): ModeCollectionIds {
  switch (mode.kind) {
    case "hub":
    case "global":
      return {};
    case "account":
      return { account: mode.id };
    case "space":
      return { account: toAccountId(mode.id), space: mode.id };
    case "event":
      return {
        account: toAccountId(mode.id),
        space: toSpaceId(mode.id),
        event: mode.id,
      };
  }
}
